<template>
    <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-medium flex items-center gap-2">
                    Roblox Campaign IDs

                    <button
                        type="button"
                        class="text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Reset all Roblox IDs to nil UUID"
                        :disabled="saving || loading || fillingDefaults || importingCsv || campaignGames.length === 0"
                        @click="showResetAllWarning = true"
                    >
                        <TrashIcon class="w-4 h-4" />
                    </button>
                </h2>

                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="fillingDefaults"
                    @click="fillDefaults"
                >
                    {{ fillingDefaults ? 'Filling...' : 'Fill Default Placements' }}
                </button>

                <button
                    type="button"
                    class="text-gray-500 hover:text-black"
                    @click="$emit('close')"
                >
                    ✕
                </button>
            </div>

            <div
                v-if="statusMessage"
                class="mb-4 rounded-md border p-3 text-sm"
                :class="statusMessageClass"
            >
                {{ statusMessage }}
            </div>

            <div
                v-if="showResetAllWarning"
                class="mb-4 bg-red-50 border border-red-200 rounded-md p-3 flex flex-col gap-3"
            >
                <p class="text-sm text-red-800">
                    This will reset every Roblox Campaign ID and every Roblox Placement ID in this campaign back to the nil UUID.
                </p>

                <div class="flex justify-end gap-2">
                    <button
                        type="button"
                        class="px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                        @click="showResetAllWarning = false"
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        class="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="saving"
                        @click="resetAllRobloxIds"
                    >
                        {{ saving ? 'Resetting...' : 'Reset All' }}
                    </button>
                </div>
            </div>

            <div v-if="loading" class="text-gray-600">
                Loading campaign games...
            </div>

            <div v-else-if="campaignGames.length === 0" class="text-gray-600">
                No campaign games found.
            </div>

            <div v-else class="flex flex-col gap-3">
                <div
                    v-for="campaignGame in campaignGames"
                    :key="campaignGame.id"
                    class="border border-gray-300 rounded-md p-4 flex flex-col gap-3"
                >
                    <div
                        v-if="activeCampaignGameId !== campaignGame.id"
                        class="grid grid-cols-1 md:grid-cols-5 gap-3 items-center"
                    >
                        <div class="flex flex-col">
                            <span class="font-medium">
                                {{ campaignGame.game_name }}
                            </span>
                            <span class="text-xs text-gray-500 break-all">
                                {{ campaignGame.game_uuid }}
                            </span>
                        </div>

                        <div class="flex flex-col">
                            <span class="text-xs text-gray-500">
                                Universe ID
                            </span>
                            <span class="text-sm">
                                {{ campaignGame.universe_id || 'N/A' }}
                            </span>
                        </div>

                        <div class="flex flex-col">
                            <span class="text-xs text-gray-500">
                                Placement IDs
                            </span>
                            <span
                                class="inline-flex w-fit rounded-full border px-2 py-0.5 text-xs font-medium"
                                :class="placementStatusClass(campaignGame.id)"
                            >
                                {{ placementStatusMessage(campaignGame.id) }}
                            </span>
                        </div>

                        <div class="flex flex-col">
                            <div class="flex items-center gap-1 mb-1">
                                <label
                                    class="text-xs"
                                    :class="campaignGameRobloxIdLabelClass(campaignGame.id)"
                                >
                                    Roblox Campaign ID
                                </label>

                                <button
                                    type="button"
                                    class="text-gray-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Reset Roblox Campaign ID to nil UUID"
                                    :disabled="saving || loading || fillingDefaults"
                                    @click="showResetCampaignGameWarning(campaignGame)"
                                >
                                    <TrashIcon class="w-4 h-4" />
                                </button>
                            </div>

                            <input
                                type="text"
                                v-model="campaignGame.roblox_campaign_id"
                                class="block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
                            />
                        </div>

                        <div class="flex justify-end">
                            <button
                                type="button"
                                class="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                                @click="openPlacementIds(campaignGame.id)"
                            >
                                Placement IDs
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="resetWarningCampaignGameId === campaignGame.id"
                        class="bg-red-50 border border-red-200 rounded-md p-3 flex flex-col gap-3"
                    >
                        <p class="text-sm text-red-800">
                            This will reset the Roblox Campaign ID for {{ campaignGame.game_name }} back to the nil UUID.
                        </p>

                        <div class="flex justify-end gap-2">
                            <button
                                type="button"
                                class="px-3 py-2 rounded-md bg-white border border-gray-300 text-sm font-medium hover:bg-gray-50"
                                @click="resetWarningCampaignGameId = null"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                class="px-3 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                                @click="resetCampaignGameRobloxCampaignId(campaignGame)"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                    <div
                        v-if="activeCampaignGameId === campaignGame.id"
                        class="flex justify-end"
                    >
                        <button
                            type="button"
                            class="px-3 py-2 rounded-md bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300"
                            @click="activeCampaignGameId = null"
                        >
                            Back
                        </button>
                    </div>

                    <CampaignGameCreativeRobloxPlacementIds
                        v-if="activeCampaignGameId === campaignGame.id"
                        :campaign-game="campaignGame"
                        :filling-defaults="fillingDefaults"
                        @fill-defaults="fillDefaults"
                        @update-placement-id="(creativeId, value) => updatePlacementId(campaignGame.id, creativeId, value)"
                        @reset-defaults="handlePlacementDefaultsReset(campaignGame.id)"
                    />

                    <div
                        v-if="activeCampaignGameId === campaignGame.id"
                        class="flex justify-end"
                    >
                        <button
                            type="button"
                            class="px-3 py-2 rounded-md bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300"
                            @click="activeCampaignGameId = null"
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>

            <div class="flex justify-end gap-3 mt-6">
                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-gray-200 text-gray-800 text-sm font-medium hover:bg-gray-300"
                    @click="$emit('close')"
                >
                    Cancel
                </button>

                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="loading || saving || fillingDefaults"
                    @click="downloadCsv"
                >
                    Download CSV Template
                </button>

                <input
                    ref="csvImportInput"
                    type="file"
                    accept=".csv,text/csv"
                    class="hidden"
                    @change="importCsv"
                />

                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="loading || saving || fillingDefaults || importingCsv"
                    @click="$refs.csvImportInput.click()"
                >
                    {{ importingCsv ? 'Importing...' : 'Import CSV' }}
                </button>

                <button
                    type="button"
                    class="px-3 py-2 rounded-md bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="saving || loading || fillingDefaults"
                    @click="save"
                >
                    {{ saving ? 'Saving...' : 'Save Roblox IDs' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { TrashIcon } from '@heroicons/vue/outline'
import CampaignGameCreativeRobloxPlacementIds from './CampaignGameCreativeRobloxPlacementIds.vue'

const DEFAULT_ROBLOX_ID = '00000000-0000-0000-0000-000000000000'

export default {
    name: 'InputCampaignGameRobloxCampaignIds',

    components: {
        CampaignGameCreativeRobloxPlacementIds,
        TrashIcon,
    },

    props: {
        show: {
            type: Boolean,
            required: true,
        },
        campaignUuid: {
            type: String,
            required: true,
        },
    },

    emits: ['close', 'saved'],

    data() {
        return {
            loading: false,
            saving: false,
            fillingDefaults: false,
            campaignGames: [],
            originalCampaignGames: {},
            originalCampaignGameCreatives: {},
            placementDefaultStatuses: {},
            activeCampaignGameId: null,
            resetWarningCampaignGameId: null,
            showResetAllWarning: false,
            importingCsv: false,
            statusMessage: '',
            statusColor: 'indigo',
            DEFAULT_ROBLOX_ID,
        }
    },

    watch: {
        async show(value) {
            if (!value) {
                this.activeCampaignGameId = null
                this.resetWarningCampaignGameId = null
                this.showResetAllWarning = false
                this.statusMessage = ''
                this.placementDefaultStatuses = {}
                return
            }

            await this.fillDefaults()
        },
    },

    computed: {
        campaignGameRobloxIdStatuses() {
            return this.campaignGames.reduce((statuses, campaignGame) => {
                const value = String(campaignGame.roblox_campaign_id || '').trim()

                statuses[campaignGame.id] = this.resolveRobloxCampaignIdStatus(value)

                return statuses
            }, {})
        },
        statusMessageClass() {
            return {
                indigo: 'border-indigo-200 bg-indigo-50 text-indigo-800',
                green: 'border-green-200 bg-green-50 text-green-800',
                yellow: 'border-yellow-200 bg-yellow-50 text-yellow-800',
                red: 'border-red-200 bg-red-50 text-red-800',
            }[this.statusColor] || 'border-gray-200 bg-gray-50 text-gray-800'
        },
    },

    methods: {
        resolveRobloxCampaignIdStatus(value) {
            if (value === DEFAULT_ROBLOX_ID) {
                return {
                    color: 'indigo',
                    labelClass: 'text-indigo-600',
                }
            }

            if (!/^\d+$/.test(value)) {
                return {
                    color: 'red',
                    labelClass: 'text-red-600',
                }
            }

            return {
                color: 'green',
                labelClass: 'text-green-600',
            }
        },

        campaignGameRobloxIdLabelClass(campaignGameId) {
            return this.campaignGameRobloxIdStatuses[campaignGameId]?.labelClass || 'text-gray-500'
        },
        showResetCampaignGameWarning(campaignGame) {
            this.resetWarningCampaignGameId = campaignGame.id
        },

        placementStatusMessage(campaignGameId) {
            return this.placementDefaultStatuses[String(campaignGameId)]?.message || 'Checking placement IDs...'
        },

        placementStatusClass(campaignGameId) {
            const color = this.placementDefaultStatuses[String(campaignGameId)]?.status_color || 'indigo'

            return {
                indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
                green: 'border-green-200 bg-green-50 text-green-700',
                red: 'border-red-200 bg-red-50 text-red-700',
            }[color] || 'border-gray-200 bg-gray-50 text-gray-700'
        },

        setPlacementDefaultStatus(campaignGameId, status) {
            this.placementDefaultStatuses = {
                ...this.placementDefaultStatuses,
                [String(campaignGameId)]: status,
            }
        },

        async openPlacementIds(campaignGameId) {
            this.activeCampaignGameId = campaignGameId
            await this.fetchCampaignGamePlacementStatus(campaignGameId)
        },

        async fetchCampaignGamePlacementStatus(campaignGameId) {
            await this.$http_request(
                this.$http.get,
                `/campaign-games/${campaignGameId}/placement-defaults/check`
            )
                .then(({ data }) => {
                    this.setPlacementDefaultStatus(campaignGameId, data)
                })
                .catch(error => {
                    this.setPlacementDefaultStatus(campaignGameId, {
                        message: 'Unable to check placement IDs.',
                        status: 'check_failed',
                        status_color: 'red',
                        complete: false,
                    })

                    console.error(error.message)
                })
        },

        async fetchCampaignGamePlacementStatuses() {
            await Promise.all(
                this.campaignGames.map(campaignGame =>
                    this.fetchCampaignGamePlacementStatus(campaignGame.id)
                )
            )
        },

        refreshLocalPlacementStatus(campaignGameId) {
            const campaignGame = this.campaignGames.find(campaignGame => campaignGame.id === campaignGameId)

            if (!campaignGame) return

            const creatives = campaignGame.campaign_game_creatives || []

            if (creatives.length === 0) {
                this.setPlacementDefaultStatus(campaignGameId, {
                    message: 'No campaign game creatives found for this campaign game.',
                    status: 'missing_campaign_game_creatives',
                    status_color: 'red',
                    complete: false,
                })

                return
            }

            const defaultCreatives = creatives.filter(creative =>
                creative.roblox_placement_id === DEFAULT_ROBLOX_ID
            )

            const allDefault = defaultCreatives.length === creatives.length
            const allSet = defaultCreatives.length === 0

            this.setPlacementDefaultStatus(campaignGameId, {
                message: allDefault
                    ? 'All placement IDs still use the default Roblox ID.'
                    : allSet
                        ? 'All placement IDs are set.'
                        : 'Some placement IDs still use the default Roblox ID.',
                status: allDefault ? 'all_default' : allSet ? 'complete' : 'incomplete',
                status_color: allDefault ? 'indigo' : allSet ? 'green' : 'red',
                complete: allSet,
            })
        },

        async handlePlacementDefaultsReset(campaignGameId) {
            await this.fetchCampaignGames()
            await this.fetchCampaignGamePlacementStatus(campaignGameId)
        },

        async resetAllRobloxIds() {
            this.saving = true

            const payload = {
                campaign_games: this.campaignGames.map(campaignGame => ({
                    id: campaignGame.id,
                    roblox_campaign_id: DEFAULT_ROBLOX_ID,
                })),
                campaign_game_creatives: this.campaignGames
                    .flatMap(campaignGame => campaignGame.campaign_game_creatives || [])
                    .map(creative => ({
                        id: creative.id,
                        roblox_placement_id: DEFAULT_ROBLOX_ID,
                    })),
            }

            const toasts = {
                success: 'All Roblox IDs reset',
                error: 'Sorry there was an error resetting Roblox IDs',
            }

            await this.$http_request(
                this.$http.put,
                `/campaigns/${this.campaignUuid}/campaign-games/roblox-campaign-ids`,
                toasts,
                payload
            )
                .then(async () => {
                    this.showResetAllWarning = false
                    this.activeCampaignGameId = null
                    this.resetWarningCampaignGameId = null

                    this.$emit('saved')

                    await this.fetchCampaignGames()
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.saving = false
                })
        },

        async resetCampaignGameRobloxCampaignId(campaignGame) {
            this.saving = true

            const payload = {
                campaign_games: [
                    {
                        id: campaignGame.id,
                        roblox_campaign_id: DEFAULT_ROBLOX_ID,
                    },
                ],
                campaign_game_creatives: [],
            }

            const toasts = {
                success: 'Roblox Campaign ID reset',
                error: 'Sorry there was an error resetting the Roblox Campaign ID',
            }

            await this.$http_request(
                this.$http.put,
                `/campaigns/${this.campaignUuid}/campaign-games/roblox-campaign-ids`,
                toasts,
                payload
            )
                .then(async () => {
                    this.resetWarningCampaignGameId = null
                    await this.fetchCampaignGames()
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.saving = false
                })
        },

        async fetchRobloxIdStatusMessage() {
            await this.$http_request(
                this.$http.get,
                `/campaigns/${this.campaignUuid}/campaign-game-creatives/check-default-roblox-ids`
            )
                .then(({ data }) => {
                    this.statusMessage = data.message || ''
                    this.statusColor = data.status_color || 'indigo'
                })
                .catch(error => {
                    this.statusMessage = ''
                    this.statusColor = 'indigo'
                    console.error(error.message)
                })
        },

        async importCsv(event) {
            const file = event.target.files?.[0]

            if (!file) return

            this.importingCsv = true

            const payload = new FormData()
            payload.append('csv', file)

            const toasts = {
                success: 'Roblox IDs imported',
                error: 'Sorry there was an error importing Roblox IDs',
            }

            await this.$http_request(
                this.$http.post,
                `/campaigns/${this.campaignUuid}/roblox-ids/import-template`,
                toasts,
                payload
            )
                .then(async () => {
                    const activeCampaignGameId = this.activeCampaignGameId

                    await this.fetchCampaignGames()

                    this.activeCampaignGameId = activeCampaignGameId
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.importingCsv = false
                    event.target.value = ''
                })
        },

        async downloadCsv() {
            try {
                const response = await this.$http.get(
                    `/campaigns/${this.campaignUuid}/roblox-ids/export-template`,
                    { responseType: 'blob' }
                )

                const disposition = response.headers['content-disposition'] || ''
                const filenameMatch = disposition.match(/filename="?([^"]+)"?/)
                const filename = filenameMatch
                    ? filenameMatch[1]
                    : `campaign-${this.campaignUuid}-roblox-template.csv`

                const blob = new Blob([response.data], { type: 'text/csv' })
                const url = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = url
                link.download = filename
                document.body.appendChild(link)
                link.click()

                link.remove()
                window.URL.revokeObjectURL(url)
            } catch (error) {
                console.error(error)
            }
        },

        async fillDefaults() {
            if (this.fillingDefaults) return

            this.fillingDefaults = true

            const toasts = {
                success: 'Missing campaign game creatives created',
                error: 'Sorry there was an error creating missing campaign game creatives',
            }

            await this.$http_request(
                this.$http.post,
                `/campaigns/${this.campaignUuid}/campaign-game-creatives/create-missing-defaults`,
                toasts
            )
                .then(async () => {
                    await this.fetchCampaignGames()
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.fillingDefaults = false
                })
        },

        async fetchCampaignGames() {
            this.loading = true

            await this.$http_request(
                this.$http.get,
                `/campaigns/${this.campaignUuid}/campaign-games/roblox-campaign-ids`
            )
                .then(async ({ data }) => {
                    this.campaignGames = data.map(campaignGame => ({
                        ...campaignGame,
                        roblox_campaign_id: campaignGame.roblox_campaign_id || DEFAULT_ROBLOX_ID,
                        campaign_game_creatives: (campaignGame.campaign_game_creatives || []).map(creative => ({
                            ...creative,
                            roblox_placement_id: creative.roblox_placement_id || DEFAULT_ROBLOX_ID,
                        })),
                    }))

                    this.originalCampaignGames = this.campaignGames.reduce((carry, campaignGame) => ({
                        ...carry,
                        [campaignGame.id]: campaignGame.roblox_campaign_id,
                    }), {})

                    this.originalCampaignGameCreatives = this.campaignGames
                        .flatMap(campaignGame => campaignGame.campaign_game_creatives || [])
                        .reduce((carry, creative) => ({
                            ...carry,
                            [creative.id]: creative.roblox_placement_id,
                        }), {})

                    await Promise.all([
                        this.fetchRobloxIdStatusMessage(),
                        this.fetchCampaignGamePlacementStatuses(),
                    ])
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.loading = false
                })
        },

        updatePlacementId(campaignGameId, campaignGameCreativeId, robloxPlacementId) {
            const campaignGame = this.campaignGames.find(campaignGame => campaignGame.id === campaignGameId)

            if (!campaignGame) return

            const creative = (campaignGame.campaign_game_creatives || []).find(creative =>
                creative.id === campaignGameCreativeId
            )

            if (!creative) return

            creative.roblox_placement_id = robloxPlacementId

            this.refreshLocalPlacementStatus(campaignGameId)
        },

        changedCampaignGames() {
            return this.campaignGames.filter(campaignGame =>
                campaignGame.roblox_campaign_id !== this.originalCampaignGames[campaignGame.id]
            )
        },

        changedCampaignGameCreatives() {
            return this.campaignGames
                .flatMap(campaignGame => campaignGame.campaign_game_creatives || [])
                .filter(creative =>
                    creative.roblox_placement_id !== this.originalCampaignGameCreatives[creative.id]
                )
        },

        async save() {
            const campaignGames = this.changedCampaignGames()
            const campaignGameCreatives = this.changedCampaignGameCreatives()

            if (campaignGames.length === 0 && campaignGameCreatives.length === 0) {
                return
            }

            this.saving = true

            const payload = {
                campaign_games: campaignGames.map(campaignGame => ({
                    id: campaignGame.id,
                    roblox_campaign_id: campaignGame.roblox_campaign_id || DEFAULT_ROBLOX_ID,
                })),
                campaign_game_creatives: campaignGameCreatives.map(creative => ({
                    id: creative.id,
                    roblox_placement_id: creative.roblox_placement_id || DEFAULT_ROBLOX_ID,
                })),
            }

            const toasts = {
                success: 'Roblox IDs updated',
                error: 'Sorry there was an error updating Roblox IDs',
            }

            await this.$http_request(
                this.$http.put,
                `/campaigns/${this.campaignUuid}/campaign-games/roblox-campaign-ids`,
                toasts,
                payload
            )
                .then(async () => {
                    this.$emit('saved')
                    await this.fetchCampaignGames()
                })
                .catch(error => {
                    console.error(error.message)
                })
                .finally(() => {
                    this.saving = false
                })
        },
    },
}
</script>